import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { SerializedError } from '@reduxjs/toolkit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeAudioBlob, toMP3 } from 'pages/Remixer/audioHelpers';
import MicrophoneProvider, {
  MicrophoneContext,
} from './components/MicrophoneProvider';
import Recorder from './components/Recorder';
import {
  StyledAcceptButton,
  StyledAudio,
  StyledMcrophoneAccessMessage,
  StyledRecorderContainer,
  StyledRecorderInner,
  StyledRecordingActions,
  StyledRejectButton,
  StyledVisualiser,
} from './styled';

interface RecordUserLoopProps {
  onAddFile: (file: File) => void;
  onError: (file: File, error: SerializedError) => void;
}

const generateFileName = () => {
  const date = new Date();
  const year = new Intl.DateTimeFormat('en', { year: '2-digit' }).format(date);
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
  const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
  const hours = new Intl.DateTimeFormat('en', {
    hour: 'numeric',
    hour12: false,
  }).format(date);
  const minutes = new Intl.DateTimeFormat('en', { minute: 'numeric' }).format(
    date
  );

  return `recording-${day}${month}${year}-${hours}${minutes}.mp3`;
};

const RecordUserLoop = ({ onAddFile, onError }: RecordUserLoopProps) => {
  const { t } = useTranslation();

  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const { audioContext, audioSource } = useContext(MicrophoneContext);

  const recordedBlobUrl = useMemo(() => {
    if (!recordedBlob) {
      return null;
    }
    return URL.createObjectURL(recordedBlob);
  }, [recordedBlob]);

  const handleAcceptBlob = useCallback(async () => {
    if (!audioContext || !recordedBlob) {
      return;
    }
    const fileName = generateFileName();
    try {
      const audioBuffer = await decodeAudioBlob(recordedBlob, audioContext);
      const mp3Buffer = toMP3(audioBuffer);
      const mp3Blob = new Blob(mp3Buffer, { type: 'audio/mpeg' });
      const file = new File([mp3Blob], fileName);

      onAddFile(file);
    } catch (error) {
      const emptyFile = new File([], fileName);
      onError(emptyFile, new Error(error as string));
    }
  }, [audioContext, onAddFile, onError, recordedBlob]);

  const handleRejectBlob = () => setRecordedBlob(null);

  const hasRecording = recordedBlob && recordedBlobUrl;

  return (
    <StyledRecorderContainer>
      {hasRecording ? (
        <StyledRecorderInner>
          <StyledAudio controls src={recordedBlobUrl} />
          <StyledRecordingActions>
            <StyledRejectButton onClick={handleRejectBlob}>
              <CloseIcon />
            </StyledRejectButton>
            <StyledAcceptButton onClick={handleAcceptBlob}>
              <CheckIcon />
            </StyledAcceptButton>
          </StyledRecordingActions>
        </StyledRecorderInner>
      ) : (
        <StyledRecorderInner>
          {audioSource ? (
            <StyledVisualiser />
          ) : (
            <StyledMcrophoneAccessMessage>
              {t('aiMusicGenerator:userLoop:microphoneAccessMessage')}
            </StyledMcrophoneAccessMessage>
          )}
          <Recorder onBlobRecorded={setRecordedBlob} />
        </StyledRecorderInner>
      )}
    </StyledRecorderContainer>
  );
};

const RecordUserLoopContainer = (props: RecordUserLoopProps) => (
  <MicrophoneProvider>
    <RecordUserLoop {...props} />
  </MicrophoneProvider>
);

export default RecordUserLoopContainer;
